/**
 * There is an issue with the Object.XXX functions in TypeScript, in that they don't respect the type of keys of the
 * objects passed into them, instead returning everything as generic string. For the most part this is fine but in some
 * specific scenarios we actually want to persist the specific types or names of those keys.
 *
 * There is a good explanation as to why TypeScript works this way with respect to objects in a few threads:
 * - https://stackoverflow.com/a/62055863
 * - https://stackoverflow.com/a/55012175
 *
 * That being said, the main takeaway and motivation for this set of functions is that
 * "
 *   if you happen to know and are absolutely sure that a value has only the keys known about by the compiler, then you
 *   can write your own typing for Object.entries() and use that instead... and you need to be very careful with it.
 * "
 *
 * So, in situations when you are manipulating objects that are static and known at build time, it is appropriate to use
 * the below functions
 *
 * You can see these functions in use in `useDictionaryObject` in
 * `apps/consumer-website/src/providers/ApplicationProvider.tsx`
 */

import { is } from '../is';
import { Dict } from '../types';

/**
 * Entries
 *
 * The return type for the entries function
 */
export type Entries<T> = { [K in keyof T]: [K, T[K]] }[keyof T];

/**
 * entries
 *
 * Returns an array of key/values of the enumerable properties of an object
 *
 * @param t Object that contains the properties and methods.
 * @returns
 */
function entries<T extends object>(t: T): Entries<T>[] {
    /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
    return Object.entries(t) as any;
}

/**
 * fromEntries
 *
 * Returns an object created by key-value entries for properties and methods
 *
 * @param t An iterable object that contains key-value entries for properties and methods.
 * @returns
 */
function fromEntries<T>(t: Entries<T>[]): { [key in keyof T]: T[key] } {
    /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
    return Object.fromEntries(t) as any;
}

/**
 * set
 *
 * Set a value to a path on an object
 *
 * @param object - Object to set the value on
 * @param path - `.` delineated path the value should be set
 * @param value - Value to set on the object
 * @returns Updated object
 */
const set = <T extends Dict<string, unknown>>(
    object: T,
    path: string,
    value: unknown,
): T => {
    const parts = path.split('.');

    // If no parts, throw error
    if (!path || !parts.length) {
        throw new Error('Invalid path');
    }

    // If only one part, set directly
    if (parts.length === 1) {
        const part = parts[0];
        return {
            ...object,
            [part]: value,
        };
    }

    // If more than one part, get the first part
    const [first, ...rest] = parts;

    const firstObject = object[first];

    // If the first part doesn't exist, create it as an object
    if (!firstObject) {
        return {
            ...object,
            [first]: set({}, rest.join('.'), value),
        };
    }
    if (!is.object(firstObject)) {
        // If the first part is not an object, throw error
        throw new Error('Cannot set path on non-object');
    }

    return {
        ...object,
        [first]: set(firstObject, rest.join('.'), value),
    };
};

type ObjectKeys<T> = T extends object
    ? (keyof T)[]
    : T extends number
    ? []
    : /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
    T extends Array<any> | string
    ? string[]
    : never;

/**
 * keys
 *
 * Returns the names of the enumerable string properties and methods of an object.
 * @param o Object that contains the properties and methods. This can be an object that you created or an existing Document Object Model (DOM) object.
 */
const keys = <T extends object>(o: T): ObjectKeys<T> =>
    /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
    Object.keys(o) as any;

/* --------------------------------- Export --------------------------------- */

export const obj = {
    entries,
    fromEntries,
    set,
    keys,
};
